// The order of the groups on the table (owner's request, November 2026): at the end of each turn they are arranged to
// use the most of the screen, even if a player's groups no longer follow one another. What counts is the height of
// the whole table, not how full one row is: three groups of 3 cards fill a first row well, and leave every group of
// 4 alone on its row, where a group of 5 with one of 4, then 4 + 3 four times, saves two rows.
// Each screen arranges its own table: the rows depend on its width.
import { useLayoutEffect, useRef, useState } from "react";

export const GROUP_GAP = 6;

// Groups have few different sizes (3, 4, 5… cards), so the search goes over how many of each are left. This only
// guards a table far fuller than a game makes (it runs while the page draws): past it, the rows are simply filled
// one after the other.
const MAX_STEPS = 60000;

const rowCapacity = (y, height, { wide, narrow, narrowFrom }) => (y + height + GROUP_GAP <= narrowFrom ? wide : narrow);

function sizesOf(groups) {
  const sizes = new Map();
  groups
    .slice()
    .sort((a, b) => a.seat - b.seat)
    .forEach((g) => {
      const key = `${g.width}x${g.height}`;
      if (!sizes.has(key)) sizes.set(key, { width: g.width, height: g.height, groups: [] });
      sizes.get(key).groups.push(g);
    });
  return [...sizes.values()].sort((a, b) => b.width - a.width);
}

// Every row these groups can make from here, taking only rows that no other group left could still join: the browser
// fills a row while something fits, so it would not leave one open anyway.
function rowsAt(sizes, counts, y, layout, onStep) {
  const rows = [];
  const picked = sizes.map(() => 0);
  const fits = (width, height) => width <= rowCapacity(y, height, layout);
  const visit = (i, width, height, used) => {
    onStep?.();
    if (i === sizes.length) {
      if (!used) return;
      const open = sizes.some((size, s) => counts[s] > picked[s]
        && fits(width + GROUP_GAP + size.width, Math.max(height, size.height)));
      if (!open && fits(width, height)) rows.push({ picked: picked.slice(), width, height });
      return;
    }
    visit(i + 1, width, height, used);
    const size = sizes[i];
    let w = width;
    let h = height;
    for (let n = 1; n <= counts[i]; n += 1) {
      w = (used + n > 1 ? w + GROUP_GAP : w) + size.width;
      h = Math.max(h, size.height);
      if (w > layout.wide) break;
      picked[i] = n;
      visit(i + 1, w, h, used + n);
    }
    picked[i] = 0;
  };
  visit(0, 0, 0, 0);
  if (rows.length) return rows;
  // Nothing fits: a group wider than the row takes a row of its own.
  const s = counts.findIndex((n) => n > 0);
  return [{ picked: sizes.map((_, i) => (i === s ? 1 : 0)), width: sizes[s].width, height: sizes[s].height }];
}

// The rows that end highest, the fullest first when two ways end at the same height. Null when the search runs long.
// Past the line every row has the same width, so what is left costs the same height wherever it starts from: `extra`
// is counted from the row's top, and only the wide rows above the line remember where they are.
function lowestRows(sizes, layout, top) {
  const memo = new Map();
  const lowest = Math.min(...sizes.map((s) => s.height));
  let steps = 0;
  const onStep = () => {
    steps += 1;
    if (steps > MAX_STEPS) throw new Error("too long");
  };
  const solve = (counts, y) => {
    if (counts.every((n) => n === 0)) return { extra: 0, rows: [] };
    const settled = layout.narrowFrom === Infinity || y + lowest + GROUP_GAP > layout.narrowFrom;
    const key = `${counts.join()}|${settled ? "" : y}`;
    if (memo.has(key)) return memo.get(key);
    let best = null;
    rowsAt(sizes, counts, y, layout, onStep).forEach((row) => {
      const taken = row.height + GROUP_GAP;
      const next = solve(counts.map((n, i) => n - row.picked[i]), y + taken);
      const extra = taken + next.extra;
      if (!best || extra < best.extra || (extra === best.extra && row.width > best.rows[0].width)) {
        best = { extra, rows: [row, ...next.rows] };
      }
    });
    memo.set(key, best);
    return best;
  };
  try {
    const { extra, rows } = solve(sizes.map((s) => s.groups.length), top);
    return { end: top + extra, rows };
  } catch {
    return null;
  }
}

// Row after row, each as full as it can be: only for a table too full to search.
function filledRows(sizes, layout, top) {
  let counts = sizes.map((s) => s.groups.length);
  let y = top;
  const rows = [];
  while (counts.some((n) => n > 0)) {
    const row = rowsAt(sizes, counts, y, layout).reduce((a, b) => (b.width > a.width ? b : a));
    rows.push(row);
    counts = counts.map((n, i) => n - row.picked[i]);
    y += row.height + GROUP_GAP;
  }
  return { end: y, rows };
}

/**
 * The order that takes the least height. `layout`: `wide`, a row's width; `narrow`, its width beside the column on a
 * phone, from `narrowFrom` down (a row crossing that line is a narrow one); `top`, where the first row starts.
 * Within a row the groups keep the order of their players. Returns the order and the height it reaches.
 */
export function packRows(groups, layout) {
  const { wide, narrow = wide, narrowFrom = Infinity, top = 0 } = layout;
  if (!groups.length) return { order: [], height: top };
  const full = { wide, narrow, narrowFrom };
  const sizes = sizesOf(groups);
  const { end, rows } = lowestRows(sizes, full, top) || filledRows(sizes, full, top);
  const next = sizes.map(() => 0);
  const order = [];
  rows.forEach((row) => {
    const members = [];
    row.picked.forEach((count, s) => {
      for (let n = 0; n < count; n += 1) {
        members.push(sizes[s].groups[next[s]]);
        next[s] += 1;
      }
    });
    members.sort((a, b) => a.seat - b.seat).forEach((g) => order.push(g.id));
  });
  return { order, height: end };
}

/** The height these groups reach in this order: each joins the row while it fits, as the browser lays them out. */
export function flowHeight(groups, layout) {
  const { wide, narrow = wide, narrowFrom = Infinity, top = 0 } = layout;
  const full = { wide, narrow, narrowFrom };
  let y = top;
  let x = 0;
  let rowHeight = 0;
  groups.forEach((g) => {
    const height = Math.max(rowHeight, g.height);
    const next = rowHeight ? x + GROUP_GAP + g.width : g.width;
    if (!rowHeight || next <= rowCapacity(y, height, full)) {
      x = next;
      rowHeight = height;
    } else {
      y += rowHeight + GROUP_GAP;
      x = g.width;
      rowHeight = g.height;
    }
  });
  return rowHeight ? y + rowHeight + GROUP_GAP : y;
}

/**
 * The new order of the groups, given in their order on screen. They only move when it gains height: otherwise each
 * new group would shuffle the whole table for nothing, and players would have to look for their groups again.
 */
export function arrangeGroups(groups, layout) {
  const packed = packRows(groups, layout);
  return packed.height < flowHeight(groups, layout) ? packed.order : groups.map((g) => g.id);
}

/**
 * The order of the table's groups on this screen, worked out when the turn changes, never during one: nothing moves
 * under the finger of the player composing a pose. `waiting`: a set is breaking (BrokenSets.jsx), arrange once it has
 * left. `room`: the place kept for the column on a phone (tableRoom.js).
 */
export function useGroupOrder({ rootRef, turnKey, waiting, room }) {
  const [order, setOrder] = useState([]);
  const roomRef = useRef(room);
  roomRef.current = room;
  const onPhone = !!room;

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (waiting || !root) return;
    const css = getComputedStyle(root);
    const inner = root.clientWidth - parseFloat(css.paddingLeft) - parseFloat(css.paddingRight);
    const placed = roomRef.current;
    // On a phone the rows hang GROUP_GAP to the left of the table, so that the first group's margin takes no room.
    const wide = placed ? inner - GROUP_GAP : inner;
    const groups = [...root.querySelectorAll("[data-combo-id]")].map((el) => ({
      id: el.getAttribute("data-combo-id"),
      seat: Number(el.getAttribute("data-combo-seat")),
      width: el.offsetWidth,
      height: el.offsetHeight,
    }));
    const next = arrangeGroups(groups, {
      wide,
      narrow: placed ? wide - placed.width : wide,
      narrowFrom: placed ? placed.top : Infinity,
      top: parseFloat(css.paddingTop),
    });
    setOrder((prev) => (prev.length === next.length && prev.every((id, i) => id === next[i]) ? prev : next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turnKey, waiting, onPhone]);

  return order;
}
